import React from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Baseline,
  Bold,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  Pencil,
  RemoveFormatting,
  Underline,
  X,
} from 'lucide-react';
import { NotesToolbarButton } from '../../../leads/components/detail/notesEditorHelpers';
import { NoteLinkModal } from '../../../leads/components/detail/NoteLinkModal';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { AppSelectDropdown } from '../../../../components/AppSelectDropdown';
import type { CompanySettings } from '../../../../types';
import { escapeHtml, sanitizeSignatureHtml } from '../../../../utils/emailBodyHtml';
import { buildAutoSignatureHtml } from '../../utils/buildAutoSignatureHtml';
import {
  preparePastedSignatureHtml,
  signatureImageDisplayWidth,
  uploadSignatureImage,
} from '../../utils/signatureImages';
import { SettingsCard } from '../../components/SettingsCard';
import { SettingsCardSavedStatus } from '../../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../../utils/settingsSectionStyles';
import type { InboxAccount } from './types';

const SIGNATURE_IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

/** Mail-safe fonts with fallbacks — recipients only see fonts installed on their device. */
const SIGNATURE_FONTS = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Calibri', value: 'Calibri, Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Segoe UI', value: "'Segoe UI', Arial, sans-serif" },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: "'Trebuchet MS', Arial, sans-serif" },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
  { label: 'Courier New', value: "'Courier New', Courier, monospace" },
];

const SIGNATURE_FONT_SIZES = [10, 11, 12, 13, 14, 15, 16, 18, 20, 24];

/** Placeholder font name: execCommand wraps the selection in <font face=MARKER>, which we turn into inline CSS. */
const FORMAT_MARKER = 'mp-signature-format';

const FONT_ATTR_BY_PROP = { 'font-family': 'face', 'font-size': 'size', color: 'color' } as const;

function UploadSpinnerIcon({ className }: { className?: string }) {
  return <LoadingSpinner className={className} />;
}

/** App dropdown trigger without its own box — looks like the toolbar buttons next to it. */
const TOOLBAR_SELECT_TRIGGER_CLASS =
  'border-transparent! bg-transparent! px-2! py-1.5! rounded! text-app-text-muted! hover:bg-slate-200! hover:text-app-text! focus:ring-0!';

/** Insert sanitized HTML at the caret without replacing the whole editor. */
function insertHtmlAtCaret(editor: HTMLElement, html: string, savedRange?: Range | null): boolean {
  const selection = window.getSelection();
  if (!selection) return false;

  let range: Range | null = null;
  if (savedRange && editor.contains(savedRange.commonAncestorContainer)) {
    range = savedRange;
  } else if (selection.rangeCount > 0) {
    const current = selection.getRangeAt(0);
    if (editor.contains(current.commonAncestorContainer)) range = current;
  }
  if (!range) {
    range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
  }

  range.deleteContents();
  const template = document.createElement('template');
  template.innerHTML = html;
  const fragment = template.content;
  const lastNode = fragment.lastChild;
  range.insertNode(fragment);
  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
  return true;
}

type Props = {
  inboxAccounts: InboxAccount[];
  onAccountsChange: (accounts: InboxAccount[]) => void;
};

export function EmailSignatureSettingsCard({ inboxAccounts, onAccountsChange }: Props) {
  const [signatureAccountId, setSignatureAccountId] = React.useState<string | null>(null);
  const [signatureDraftHtml, setSignatureDraftHtml] = React.useState('');
  const [signatureSaving, setSignatureSaving] = React.useState(false);
  const [signatureSaved, setSignatureSaved] = React.useState(false);
  const [signatureError, setSignatureError] = React.useState<string | null>(null);
  const [signatureNotice, setSignatureNotice] = React.useState<string | null>(null);
  const [imageUploading, setImageUploading] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<HTMLImageElement | null>(null);
  /** Selected image's box relative to the editor wrapper — positions the resize handles. */
  const [imageBox, setImageBox] = React.useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [resizingWidth, setResizingWidth] = React.useState<number | null>(null);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [linkModalText, setLinkModalText] = React.useState('');
  const [linkModalUrl, setLinkModalUrl] = React.useState('');
  const [showLinkHint, setShowLinkHint] = React.useState(false);
  const linkSavedRangeRef = React.useRef<Range | null>(null);
  const signatureEditorRef = React.useRef<HTMLDivElement>(null);
  const editorWrapperRef = React.useRef<HTMLDivElement>(null);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  /** Caret inside the editor — restored for async inserts (uploads) after focus moved away. */
  const savedRangeRef = React.useRef<Range | null>(null);
  const companyForSignatureRef = React.useRef<CompanySettings | null>(null);

  React.useEffect(() => {
    if (inboxAccounts.length === 0) {
      setSignatureAccountId(null);
      setSignatureDraftHtml('');
      if (signatureEditorRef.current) signatureEditorRef.current.innerHTML = '';
      return;
    }
    setSignatureAccountId((prev) => {
      if (prev != null && inboxAccounts.some((a) => String(a.id) === prev)) return prev;
      return String(inboxAccounts[0].id);
    });
  }, [inboxAccounts]);

  React.useEffect(() => {
    if (signatureAccountId == null) return;
    const selected = inboxAccounts.find((a) => String(a.id) === signatureAccountId);
    if (!selected) return;
    const html = sanitizeSignatureHtml(selected.signature_html || '');
    setSignatureDraftHtml(html);
    setSignatureSaved(false);
    setSignatureError(null);
    setSignatureNotice(null);
    setSelectedImage(null);
    if (signatureEditorRef.current && signatureEditorRef.current.innerHTML !== html) {
      signatureEditorRef.current.innerHTML = html;
    }
  }, [signatureAccountId, inboxAccounts]);

  const handleSignatureEditorInput = () => {
    const html = signatureEditorRef.current?.innerHTML || '';
    setSignatureDraftHtml(html);
    setSignatureSaved(false);
    setSignatureError(null);
    setSelectedImage((img) => (img && signatureEditorRef.current?.contains(img) ? img : null));
  };

  const saveEditorRange = () => {
    const editor = signatureEditorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) savedRangeRef.current = range.cloneRange();
  };

  const insertSanitizedHtml = (sanitized: string) => {
    const editor = signatureEditorRef.current;
    if (!editor || !sanitized) return;
    editor.focus();
    const saved = savedRangeRef.current;
    const selection = window.getSelection();
    if (saved && selection && editor.contains(saved.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(saved);
    }
    const ok = document.execCommand('insertHTML', false, sanitized);
    if (!ok) insertHtmlAtCaret(editor, sanitized, saved);
    saveEditorRange();
    handleSignatureEditorInput();
  };

  const insertUploadedImages = async (files: File[]) => {
    if (files.length === 0) return;
    setImageUploading(true);
    setSignatureError(null);
    try {
      const tags: string[] = [];
      for (const file of files) {
        const [url, width] = await Promise.all([
          uploadSignatureImage(file, file.name),
          signatureImageDisplayWidth(file),
        ]);
        tags.push(`<img src="${escapeHtml(url)}" alt=""${width ? ` width="${width}"` : ''}>`);
      }
      const sanitized = sanitizeSignatureHtml(tags.join(''));
      // Upload URL not public https (e.g. misconfigured APP_URL) → sanitizer drops it; say so.
      if (!sanitized.includes('<img')) {
        throw new Error('Bild wurde hochgeladen, aber die Bild-Adresse ist nicht öffentlich erreichbar (kein https).');
      }
      insertSanitizedHtml(sanitized);
      setSignatureNotice(null);
    } catch (e) {
      setSignatureError(e instanceof Error ? e.message : 'Bild konnte nicht hochgeladen werden.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSignaturePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const imageFiles = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (!html && !text && imageFiles.length === 0) return;
    e.preventDefault();
    saveEditorRange();
    setSignatureNotice(null);
    // Screenshot / copied image file without HTML around it → upload it.
    if (!html && imageFiles.length > 0) {
      void insertUploadedImages(imageFiles);
      return;
    }
    if (!html) {
      insertSanitizedHtml(
        sanitizeSignatureHtml(escapeHtml(text).replace(/\r\n/g, '\n').replace(/\n/g, '<br/>')),
      );
      return;
    }
    setImageUploading(true);
    void preparePastedSignatureHtml(html)
      .then(({ html: prepared, droppedImages, uploadErrors }) => {
        // Same sanitize as save: keeps fonts/colours/public images, emoji <img> → glyphs.
        insertSanitizedHtml(sanitizeSignatureHtml(prepared));
        if (droppedImages > 0) {
          setSignatureNotice(
            `${droppedImages === 1 ? '1 Bild konnte' : `${droppedImages} Bilder konnten`} nicht übernommen werden – ` +
              'sie sind nur mit Login im ursprünglichen Postfach abrufbar (z. B. Gmail). ' +
              'Bitte das Bild speichern und über „Bild einfügen“ hochladen.' +
              (uploadErrors.length > 0 ? ` (${uploadErrors[0]})` : ''),
          );
        }
      })
      .finally(() => setImageUploading(false));
  };

  /**
   * Font, size or colour for the selection — or the whole signature when nothing is selected.
   * Written as inline CSS (mail clients ignore classes); nested overrides of the same property are removed.
   */
  const applyInlineStyle = (prop: keyof typeof FONT_ATTR_BY_PROP, value: string) => {
    const editor = signatureEditorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || !value) return;
    editor.focus();
    const saved = savedRangeRef.current;
    let range = saved && editor.contains(saved.commonAncestorContainer) ? saved.cloneRange() : null;
    const wholeSignature = !range || range.collapsed;
    if (wholeSignature) {
      range = document.createRange();
      range.selectNodeContents(editor);
    }
    selection.removeAllRanges();
    selection.addRange(range!);
    // fontName may rewrite an existing <font face>; keep its font when changing size/colour.
    const originalFaces = new Map(
      Array.from(editor.querySelectorAll('font[face]')).map((el) => [el, el.getAttribute('face')] as const),
    );
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand('fontName', false, FORMAT_MARKER);
    const fontAttr = FONT_ATTR_BY_PROP[prop];
    for (const el of Array.from(editor.querySelectorAll<HTMLElement>(`font[face="${FORMAT_MARKER}"]`))) {
      const originalFace = originalFaces.get(el);
      if (originalFace && prop !== 'font-family') el.setAttribute('face', originalFace);
      else el.removeAttribute('face');
      el.style.setProperty(prop, value);
      for (const inner of Array.from(el.querySelectorAll<HTMLElement>('*'))) {
        inner.style.removeProperty(prop);
        if (inner.tagName === 'FONT') inner.removeAttribute(fontAttr);
        if (!inner.getAttribute('style')) inner.removeAttribute('style');
      }
    }
    if (wholeSignature) selection.collapseToEnd();
    saveEditorRange();
    handleSignatureEditorInput();
  };

  const applyInlineStyleRef = React.useRef(applyInlineStyle);
  applyInlineStyleRef.current = applyInlineStyle;
  /** Native `change` fires once when the colour picker closes (React's onChange fires while dragging). */
  const colorInputRef = React.useCallback((el: HTMLInputElement | null) => {
    if (el) el.onchange = () => applyInlineStyleRef.current('color', el.value);
  }, []);

  React.useEffect(() => {
    if (!showLinkHint) return;
    const t = setTimeout(() => setShowLinkHint(false), 2500);
    return () => clearTimeout(t);
  }, [showLinkHint]);

  const openLinkModal = () => {
    const sel = window.getSelection();
    const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
    const text = range?.toString().trim() ?? '';
    if (!range || !text) {
      setShowLinkHint(true);
      return;
    }
    linkSavedRangeRef.current = range.cloneRange();
    setLinkModalText(text);
    setLinkModalUrl('');
    setShowLinkModal(true);
  };

  const saveLink = () => {
    const url = linkModalUrl.trim();
    if (!url) return;
    const text = linkModalText.trim();
    signatureEditorRef.current?.focus();
    const sel = window.getSelection();
    const range = linkSavedRangeRef.current;
    if (sel && range) {
      sel.removeAllRanges();
      sel.addRange(range);
      if (text && text !== range.toString()) {
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        const next = document.createRange();
        next.selectNodeContents(textNode);
        sel.removeAllRanges();
        sel.addRange(next);
      }
      document.execCommand('createLink', false, url);
      handleSignatureEditorInput();
    }
    setShowLinkModal(false);
  };

  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setSelectedImage(e.target instanceof HTMLImageElement ? e.target : null);
  };

  const removeSelectedImage = () => {
    selectedImage?.remove();
    setSelectedImage(null);
    handleSignatureEditorInput();
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!selectedImage) return;
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      removeSelectedImage();
    } else if (e.key === 'Escape') {
      setSelectedImage(null);
    }
  };

  const measureSelectedImage = React.useCallback(() => {
    const wrapper = editorWrapperRef.current;
    if (!selectedImage || !wrapper || !wrapper.contains(selectedImage)) {
      setImageBox(null);
      return;
    }
    const outer = wrapper.getBoundingClientRect();
    const r = selectedImage.getBoundingClientRect();
    setImageBox({ left: r.left - outer.left, top: r.top - outer.top, width: r.width, height: r.height });
  }, [selectedImage]);

  // Keep the handles on the image while text around it changes, the editor scrolls or the window resizes.
  React.useLayoutEffect(() => {
    measureSelectedImage();
    const editor = signatureEditorRef.current;
    if (!selectedImage || !editor) return;
    const observer = new ResizeObserver(measureSelectedImage);
    observer.observe(selectedImage);
    observer.observe(editor);
    editor.addEventListener('scroll', measureSelectedImage);
    window.addEventListener('resize', measureSelectedImage);
    selectedImage.addEventListener('load', measureSelectedImage);
    return () => {
      observer.disconnect();
      editor.removeEventListener('scroll', measureSelectedImage);
      window.removeEventListener('resize', measureSelectedImage);
      selectedImage.removeEventListener('load', measureSelectedImage);
    };
  }, [selectedImage, measureSelectedImage, signatureDraftHtml]);

  // Click outside the editor → deselect the image.
  React.useEffect(() => {
    if (!selectedImage) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!editorWrapperRef.current?.contains(e.target as Node)) setSelectedImage(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [selectedImage]);

  /** Drag a corner handle: width follows the mouse, height follows the aspect ratio. */
  const startImageResize = (e: React.PointerEvent, direction: 1 | -1) => {
    const img = selectedImage;
    const editor = signatureEditorRef.current;
    if (!img || !editor) return;
    e.preventDefault();
    e.stopPropagation();
    const style = getComputedStyle(editor);
    const contentWidth = editor.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const maxWidth = Math.max(8, Math.min(1200, Math.floor(contentWidth)));
    const startX = e.clientX;
    const startWidth = img.getBoundingClientRect().width;
    const onMove = (ev: PointerEvent) => {
      const width = Math.round(Math.min(maxWidth, Math.max(8, startWidth + direction * (ev.clientX - startX))));
      img.setAttribute('width', String(width));
      img.removeAttribute('height');
      img.style.removeProperty('width');
      img.style.removeProperty('height');
      setResizingWidth(width);
      measureSelectedImage();
    };
    const onEnd = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      setResizingWidth(null);
      handleSignatureEditorInput();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  };

  const loadCompanyForSignature = async (): Promise<CompanySettings | null> => {
    if (companyForSignatureRef.current) return companyForSignatureRef.current;
    try {
      const res = await fetch('/api/settings/company', { credentials: 'include' });
      if (!res.ok) return null;
      const data = (await res.json()) as CompanySettings;
      companyForSignatureRef.current = data;
      return data;
    } catch {
      return null;
    }
  };

  const handleSignatureAutogenerate = async () => {
    const company = await loadCompanyForSignature();
    if (!company) {
      setSignatureError('Unternehmensdaten konnten nicht geladen werden.');
      return;
    }
    const html = buildAutoSignatureHtml(company);
    if (signatureEditorRef.current) signatureEditorRef.current.innerHTML = html;
    setSignatureDraftHtml(html);
    setSignatureSaved(false);
    setSignatureError(null);
  };

  const handleSignatureClear = () => {
    if (signatureEditorRef.current) signatureEditorRef.current.innerHTML = '';
    setSignatureDraftHtml('');
    setSignatureSaved(false);
    setSignatureError(null);
    setSignatureNotice(null);
    setSelectedImage(null);
  };

  const handleSignatureSave = async () => {
    if (signatureAccountId == null) return;
    const latestEditorHtml = signatureEditorRef.current?.innerHTML ?? signatureDraftHtml;
    setSignatureDraftHtml(latestEditorHtml);
    setSignatureSaving(true);
    setSignatureSaved(false);
    setSignatureError(null);
    try {
      const res = await fetch(`/api/inbox/accounts/${signatureAccountId}/signature`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signatureHtml: latestEditorHtml }),
      });
      const contentType = String(res.headers.get('content-type') || '').toLowerCase();
      const data = contentType.includes('application/json') ? await res.json().catch(() => null) : null;
      if (!res.ok) {
        throw new Error((data as { error?: string } | null)?.error || 'Signatur konnte nicht gespeichert werden.');
      }
      const returnedId = (data as { id?: unknown } | null)?.id;
      if (!data || (typeof returnedId !== 'number' && typeof returnedId !== 'string')) {
        throw new Error('Unerwartete Server-Antwort. Bitte Server neu starten und erneut speichern.');
      }
      const updated = data as InboxAccount;
      onAccountsChange(
        inboxAccounts.map((acc) => (String(acc.id) === String(updated.id) ? { ...acc, ...updated } : acc)),
      );
      if (signatureEditorRef.current) signatureEditorRef.current.innerHTML = updated.signature_html || '';
      setSignatureDraftHtml(updated.signature_html || '');
      setSignatureSaved(true);
      setTimeout(() => setSignatureSaved(false), 2200);
    } catch (e) {
      setSignatureError(e instanceof Error ? e.message : 'Signatur konnte nicht gespeichert werden.');
    } finally {
      setSignatureSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Signatur"
      description="HTML-Signatur pro Postfach. Wird beim manuellen Versand und bei Agent-Mails automatisch angehängt."
      actions={
        <button
          type="button"
          onClick={() => void handleSignatureAutogenerate()}
          disabled={signatureAccountId == null}
          className={SETTINGS_BTN_PRIMARY}
          title="Signatur aus Unternehmensdaten erzeugen"
        >
          <Pencil className="w-4 h-4" />
          Automatisch erstellen
        </button>
      }
    >
      {inboxAccounts.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Verbinden Sie zuerst ein Postfach unter <span className="font-medium">Verbundene Postfächer</span>.
        </div>
      ) : (
        <>
          <div className="mb-3">
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Postfach</label>
            <AppSelectDropdown
              value={signatureAccountId != null ? String(signatureAccountId) : ''}
              onChange={(v) => setSignatureAccountId(v || null)}
              options={[
                { value: '', label: 'Bitte wählen …' },
                ...inboxAccounts.map((acc) => ({ value: String(acc.id), label: acc.email })),
              ]}
              placeholder="Bitte wählen …"
              className="w-full min-w-0"
              triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              disabled={inboxAccounts.length === 0}
            />
          </div>

          <div className="mb-3">
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              Signatur (HTML / Paste mit Formatierung)
            </label>
            {/* White like a mail: pasted signatures carry their own (usually black) text colour. */}
            <div ref={editorWrapperRef} className="relative rounded-md border border-app-border bg-white">
              {signatureDraftHtml.trim() === '' ? (
                <span className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400">
                  Signatur hier einfügen oder formatieren …
                </span>
              ) : null}
              <div
                ref={signatureEditorRef}
                contentEditable={signatureAccountId != null}
                suppressContentEditableWarning
                onInput={handleSignatureEditorInput}
                onPaste={handleSignaturePaste}
                onClick={handleEditorClick}
                onKeyDown={handleEditorKeyDown}
                onKeyUp={saveEditorRange}
                onMouseUp={saveEditorRange}
                onBlur={saveEditorRange}
                className="min-h-[140px] w-full overflow-x-auto px-3 py-2 text-sm text-slate-900 outline-none [&_a]:text-blue-600 [&_a]:underline [&_img]:inline-block [&_img]:max-w-full [&_img]:cursor-pointer"
              />
              {selectedImage && imageBox ? (
                <div
                  className="pointer-events-none absolute border-2 border-blue-500"
                  style={{ left: imageBox.left, top: imageBox.top, width: imageBox.width, height: imageBox.height }}
                >
                  {(
                    [
                      ['-left-1.5 -top-1.5 cursor-nwse-resize', -1],
                      ['-right-1.5 -top-1.5 cursor-nesw-resize', 1],
                      ['-left-1.5 -bottom-1.5 cursor-nesw-resize', -1],
                      ['-right-1.5 -bottom-1.5 cursor-nwse-resize', 1],
                    ] as const
                  ).map(([position, direction]) => (
                    <span
                      key={position}
                      onPointerDown={(e) => startImageResize(e, direction)}
                      className={`pointer-events-auto absolute h-3 w-3 touch-none rounded-sm border border-white bg-blue-500 shadow ${position}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={removeSelectedImage}
                    title="Bild entfernen (Entf)"
                    className="pointer-events-auto absolute -top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full border border-app-border bg-white text-slate-600 shadow hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {resizingWidth != null ? (
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-1.5 py-0.5 text-xs text-white">
                      {resizingWidth} px
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-app-border px-2 py-1.5">
                <div className="flex flex-wrap items-center gap-0.5">
                  {/* Value stays empty: an action picker, not a state display (selections can mix fonts). */}
                  <AppSelectDropdown
                    value=""
                    onChange={(v) => applyInlineStyle('font-family', v)}
                    options={SIGNATURE_FONTS}
                    placeholder="Schriftart"
                    className="w-36"
                    triggerClassName={TOOLBAR_SELECT_TRIGGER_CLASS}
                  />
                  <AppSelectDropdown
                    value=""
                    onChange={(v) => applyInlineStyle('font-size', v)}
                    options={SIGNATURE_FONT_SIZES.map((size) => ({ value: `${size}px`, label: `${size} px` }))}
                    placeholder="Größe"
                    className="w-24"
                    triggerClassName={TOOLBAR_SELECT_TRIGGER_CLASS}
                  />
                  <span className="w-px h-5 bg-slate-200 mx-1" />
                  <NotesToolbarButton editorRef={signatureEditorRef} savedRangeRef={savedRangeRef} onCommand="bold" title="Fett" icon={Bold} />
                  <NotesToolbarButton editorRef={signatureEditorRef} savedRangeRef={savedRangeRef} onCommand="italic" title="Kursiv" icon={Italic} />
                  <NotesToolbarButton editorRef={signatureEditorRef} savedRangeRef={savedRangeRef} onCommand="underline" title="Unterstrichen" icon={Underline} />
                  <label
                    title="Textfarbe (ohne Markierung: ganze Signatur)"
                    className="relative p-1.5 rounded text-app-text-muted hover:bg-slate-200 hover:text-app-text cursor-pointer"
                  >
                    <Baseline className="w-4 h-4" />
                    <input
                      ref={colorInputRef}
                      type="color"
                      defaultValue="#000000"
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    />
                  </label>
                  <span className="w-px h-5 bg-slate-200 mx-1" />
                  <span className="relative inline-block">
                    <NotesToolbarButton title="Link einfügen" icon={LinkIcon} onClick={openLinkModal} />
                    {showLinkHint ? (
                      <div className="absolute left-0 bottom-full mb-1 px-2 py-1.5 text-xs text-white bg-slate-700 rounded shadow-lg whitespace-nowrap z-50">
                        Bitte zuerst Text markieren, der verlinkt werden soll.
                      </div>
                    ) : null}
                  </span>
                  <NotesToolbarButton
                    editorRef={signatureEditorRef}
                    savedRangeRef={savedRangeRef}
                    title="Logo oder Siegel als Bild einfügen"
                    icon={imageUploading ? UploadSpinnerIcon : ImagePlus}
                    onClick={() => {
                      if (!imageUploading) imageInputRef.current?.click();
                    }}
                  />
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept={SIGNATURE_IMAGE_ACCEPT}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.target.value = '';
                      void insertUploadedImages(files);
                    }}
                  />
                  <span className="w-px h-5 bg-slate-200 mx-1" />
                  <NotesToolbarButton editorRef={signatureEditorRef} savedRangeRef={savedRangeRef} onCommand="justifyLeft" title="Linksbündig" icon={AlignLeft} />
                  <NotesToolbarButton editorRef={signatureEditorRef} savedRangeRef={savedRangeRef} onCommand="justifyCenter" title="Zentriert" icon={AlignCenter} />
                  <NotesToolbarButton editorRef={signatureEditorRef} savedRangeRef={savedRangeRef} onCommand="justifyRight" title="Rechtsbündig" icon={AlignRight} />
                  <span className="w-px h-5 bg-slate-200 mx-1" />
                  <NotesToolbarButton
                    editorRef={signatureEditorRef}
                    savedRangeRef={savedRangeRef}
                    onCommand="removeFormat"
                    title="Formatierung der Markierung entfernen"
                    icon={RemoveFormatting}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <SettingsCardSavedStatus saved={signatureSaved}>Gespeichert</SettingsCardSavedStatus>
                  <button
                    type="button"
                    onClick={handleSignatureClear}
                    disabled={signatureAccountId == null || signatureSaving}
                    className={SETTINGS_BTN_OUTLINE}
                  >
                    Leeren
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSignatureSave()}
                    disabled={
                      signatureAccountId == null ||
                      signatureSaving ||
                      signatureDraftHtml ===
                        sanitizeSignatureHtml(
                          inboxAccounts.find((a) => String(a.id) === signatureAccountId)?.signature_html || '',
                        )
                    }
                    className={SETTINGS_BTN_PRIMARY}
                  >
                    {signatureSaving ? (
                      <>
                        <LoadingSpinner />
                        Speichert...
                      </>
                    ) : (
                      'Signatur speichern'
                    )}
                  </button>
                </div>
              </div>
            </div>
            {signatureNotice ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {signatureNotice}
              </div>
            ) : null}
            <NoteLinkModal
              open={showLinkModal}
              text={linkModalText}
              url={linkModalUrl}
              onTextChange={setLinkModalText}
              onUrlChange={setLinkModalUrl}
              onClose={() => setShowLinkModal(false)}
              onSave={saveLink}
            />
          </div>

          {signatureError ? (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {signatureError}
            </div>
          ) : null}
        </>
      )}
    </SettingsCard>
  );
}
